import { useState } from 'react';
import { api } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';

type Saved = { saved: boolean; readable: boolean; hint: string };
type Video = {
  r2_account_id: string; r2_bucket: string; r2_access_key: string; r2_ttl_min: number;
  stream_customer: string; stream_key_id: string; stream_ttl_min: number; cf_account_id: string;
  r2_secret: Saved; stream_pem: Saved; cf_api_token: Saved;
  r2_ready: boolean; stream_ready: boolean; can_store_secrets: boolean;
  counts: { r2: number; stream: number; url: number; none: number };
};

/**
 * Where lesson videos are stored. Cloudflare R2 today; Stream is ready to fill
 * in the day we move. Secrets are stored sealed and never shown again, only
 * their last four characters.
 */
export function VideoStorage() {
  const { data, error, reload } = useAsync((signal) => api.get<{ video: Video }>('/video', signal));
  const [form, setForm] = useState<Record<string, string>>({});
  const [testKey, setTestKey] = useState('');
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(false);

  const v = data?.video;
  if (error) return <div className="err">{String((error as Error).message)}</div>;
  if (!v) return null;

  const val = (k: keyof Video) => form[k] ?? String(v[k] ?? '');
  const set = (k: string, x: string) => setForm((f) => ({ ...f, [k]: x }));

  async function act(what: string, fn: () => Promise<string>) {
    setBusy(what); setNote('');
    try { setNote(await fn()); await reload(); } catch (e) { setNote((e as Error).message); }
    setBusy('');
  }
  const save = () => act('save', async () => {
    await api.put('/video', form);
    setForm({});
    return 'Saved.';
  });
  const secret = (k: 'r2_secret' | 'stream_pem' | 'cf_api_token', label: string, placeholder: string) => (
    <div className="fld"><label className="lbl">{label} {v[k].saved ? `(saved ${v[k].hint})` : ''}</label>
      <input className="inp" type="password" autoComplete="new-password" value={form[k] ?? ''}
        onChange={(e) => set(k, e.target.value)}
        placeholder={v[k].saved ? 'Leave blank to keep the saved one' : placeholder} /></div>
  );
  const text = (k: keyof Video, label: string, placeholder = '') => (
    <div className="fld"><label className="lbl">{label}</label>
      <input className="inp" value={val(k)} onChange={(e) => set(k, e.target.value)} placeholder={placeholder} /></div>
  );
  const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 14 } as const;

  return (
    <div className="crd" style={{ padding: '16px 22px', marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <div className="kh" style={{ marginBottom: 4 }}>Video storage</div>
          <div className="muted" style={{ fontSize: 12.5 }}>
            R2 {v.r2_ready ? 'is set up' : 'is not set up yet'} · Stream {v.stream_ready ? 'is set up' : 'is not set up'} ·
            lessons: {v.counts.r2} on R2, {v.counts.stream} on Stream, {v.counts.url} with a plain link, {v.counts.none} with no video
          </div>
        </div>
        <button className="b bo" type="button" onClick={() => setOpen(!open)}>{open ? 'Close' : 'Set up'}</button>
      </div>

      {open ? (
        <div style={{ marginTop: 14, display: 'grid', gap: 16 }}>
          <p className="hint" style={{ margin: 0, lineHeight: 1.6 }}>
            In a lesson&apos;s video box, write <b>r2:courses/forex-basic/01.mp4</b> (the file&apos;s path in the bucket) or, later,
            <b> stream:&lt;video id&gt;</b>. Members never see that name: they are handed a link that stops working after a few hours.
          </p>
          {!v.can_store_secrets ? <div className="err">The server has no TVX_SECRET set, so keys cannot be stored safely.</div> : null}

          <div>
            <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 8 }}>Cloudflare R2</div>
            <p className="hint" style={{ margin: '0 0 10px' }}>
              In Cloudflare: R2 → Manage API tokens → Create token with <b>Object Read &amp; Write</b> on your bucket.
              Paste the Access Key ID and Secret Access Key it shows once.
            </p>
            <div style={grid}>
              {text('r2_account_id', 'Account ID', '32 letters and numbers')}
              {text('r2_bucket', 'Bucket name', 'tradevix-courses')}
              {text('r2_access_key', 'Access Key ID')}
              {secret('r2_secret', 'Secret Access Key', 'Paste the secret')}
              {text('r2_ttl_min', 'Link lasts (minutes)', '240')}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12, alignItems: 'center' }}>
              <input className="inp" style={{ maxWidth: 320 }} value={testKey} onChange={(e) => setTestKey(e.target.value)}
                placeholder="A file already in the bucket, e.g. courses/test.mp4" />
              <button className="b bo" type="button" disabled={busy !== '' || !v.r2_ready || !testKey.trim()}
                onClick={() => void act('test', async () => {
                  const r = await api.post<{ bytes: number }>('/video/test', { key: testKey });
                  return `Works. R2 would serve that file (${Math.round(r.bytes / 1048576)} MB).`;
                })}>{busy === 'test' ? 'Testing…' : 'Test'}</button>
            </div>
          </div>

          <div>
            <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 8 }}>Cloudflare Stream — fill in when you switch</div>
            <p className="hint" style={{ margin: '0 0 10px' }}>
              Stream → Signing keys → Create. Copy the key id and the key. The customer code is in any Stream video&apos;s
              embed link: <b>customer-<u>xxxx</u>.cloudflarestream.com</b>. The API token (Stream edit) is only used by the
              move-from-R2 script.
            </p>
            <div style={grid}>
              {text('stream_customer', 'Customer code')}
              {text('stream_key_id', 'Signing key id')}
              {secret('stream_pem', 'Signing key', 'Paste the key (base64 or PEM)')}
              {text('cf_account_id', 'Account ID (for the move)', '32 letters and numbers')}
              {secret('cf_api_token', 'API token (Stream edit)', 'Only needed to move videos')}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="b bg" type="button" disabled={busy !== '' || !Object.keys(form).length} onClick={() => void save()}>
              {busy === 'save' ? 'Saving…' : 'Save'}
            </button>
          </div>
          {note ? <div className="crd" style={{ padding: '8px 14px', fontSize: 12.5 }}>{note}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
